import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from './api/axios';
// --- UPDATED --- Added Header and useAuth
import Header from './Header'; // Using the global Header
import { useAuth } from './AuthContext'; // Using global AuthContext
import { FaExclamationTriangle, FaSyncAlt, FaArrowLeft } from 'react-icons/fa'; // Added FaArrowLeft

// --- Helper Components & Icons ---

// --- UPDATED --- Using React Icons instead of SVG strings for consistency
// const ThumbsDownIcon = () => ( ... ); // Replaced with FaExclamationTriangle
// const RefreshIcon = () => ( ... ); // Replaced with FaSyncAlt

// --- UPDATED --- Styled LoadingSpinner for dark theme
const LoadingSpinner = () => (
    <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p> {/* Added text */}
    </div>
);

// --- UPDATED --- Styled ErrorMessage for dark theme
const ErrorMessage = ({ message }) => (
    <div className="form-feedback error"> {/* Changed class */}
        {/* <p className="error-title">An Error Occurred</p> // Removed redundant title */}
        <p>{message}</p>
    </div>
);

const formatErrorDate = (dateString) => {
    if (!dateString) return 'an unknown date';
    // Swap the space for a 'T' to ensure strict browser compatibility
    const safeDateString = typeof dateString === 'string' && !dateString.includes('T') 
        ? dateString.replace(' ', 'T') 
        : dateString;
        
    const dateObj = new Date(safeDateString);
    return isNaN(dateObj) ? 'an unknown date' : dateObj.toLocaleString();
};

// --- DYNAMIC Helper: Error Grouping Logic ---
const groupErrorItems = (items, maxRowsPerPattern) => {
    const grouped = {};
    const generalErrors = [];
    let backendOmittedCount = 0;

    items.forEach(item => {
        // 1. Catch the backend omission warning and extract the number mathematically
        if (item.startsWith("... and") && item.includes("omitted")) {
            const match = item.match(/\.\.\. and (\d+) more/);
            if (match) {
                backendOmittedCount += parseInt(match[1], 10);
            }
            return; // Skip adding this line to the final output entirely
        }

        // 2. Extract Row/Line errors
        // Matches "Row 123: Error" or "Row 123, Column 'X': Error" or "Line 123 is..."
        const match = item.match(/^(?:Row|Line)\s+(\d+)(?:[:\s,]+)(.*)$/i);
        if (match) {
            const rowNum = parseInt(match[1], 10);
            const errorDesc = match[2].trim();
            
            if (!grouped[errorDesc]) grouped[errorDesc] = [];
            grouped[errorDesc].push(rowNum);
        } else {
            // For Header errors or global errors without a row number
            generalErrors.push(item);
        }
    });

    const result = [...generalErrors];
    
    // Sort error patterns by how many rows they affect (descending)
    // We do this so if there are multiple patterns, the omitted count gets added to the biggest one
    const entries = Object.entries(grouped).sort((a, b) => b[1].length - a[1].length);

    for (let i = 0; i < entries.length; i++) {
        const [desc, rows] = entries[i];
        rows.sort((a, b) => a - b);
        
        let displayedRows = rows;
        let remaining = 0;

        // Calculate UI slicing based on the passed limits (10 for accordion, 100 for modal, Infinity for export)
        if (maxRowsPerPattern && maxRowsPerPattern !== Infinity && rows.length > maxRowsPerPattern) {
            displayedRows = rows.slice(0, maxRowsPerPattern);
            remaining = rows.length - maxRowsPerPattern;
        }

        // 3. Inject the backend omitted count into the largest grouped pattern!
        if (i === 0 && backendOmittedCount > 0) {
            remaining += backendOmittedCount;
        }

        if (displayedRows.length === 1 && remaining === 0) {
            if (desc.includes("is an exact duplicate")) {
                result.push(`Line ${displayedRows[0]} ${desc}`);
            } else {
                let cleanDesc = desc.replace(/^:\s*/, '');
                result.push(`Row ${displayedRows[0]}: ${cleanDesc}`);
            }
        } else {
            const displayed = displayedRows.join(', ');
            const rowStr = remaining > 0 
                ? `Rows ${displayed} and ${remaining.toLocaleString()} more rows` 
                : `Rows ${displayed}`;
            
            if (desc.includes("is an exact duplicate")) {
                // Pluralize grammar for duplicates
                let pluralDesc = desc.replace("is an exact duplicate", "are exact duplicates");
                result.push(`${rowStr} ${pluralDesc}`);
            } else {
                // Standard row errors
                let cleanDesc = desc.replace(/^:\s*/, '');
                result.push(`${rowStr}: ${cleanDesc}`);
            }
        }
    }

    // Safety fallback: if there were no row patterns but the backend omitted something
    if (entries.length === 0 && backendOmittedCount > 0) {
        result.push(`... and ${backendOmittedCount.toLocaleString()} more errors of this type omitted to save storage.`);
    }

    return result;
};

// isDeadlineApproaching logic remains unchanged
const isDeadlineApproaching = (dateString) => {
    if (!dateString) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(dateString + 'T00:00:00Z');
    if (isNaN(endDate.getTime())) {
        console.warn("Invalid date string encountered:", dateString);
        return false;
    }
    const daysDiff = (endDate.getTime() - today.getTime()) / (1000 * 3600 * 24);
    return daysDiff >= 0 && daysDiff <= 2;
};


// ValidationErrorModal logic remains unchanged, styling is handled in <style>
const ValidationErrorModal = ({ isOpen, onClose, interfaceName, errors, isLoading, fetchError }) => {
    const groupedErrors = useMemo(() => {
        const errorList = errors || [];
        const groups = errorList.reduce((acc, error) => {
            if (!acc[error.filename]) {
                acc[error.filename] = [];
            }
            acc[error.filename].push(error);
            return acc;
        }, {});
        return Object.entries(groups);
    }, [errors]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Validation Errors for {interfaceName}</h3>
                    <button onClick={onClose} className="modal-close-button">&times;</button>
                </div>
                <div className="modal-body">
                    {isLoading && <LoadingSpinner />}
                    {fetchError && <ErrorMessage message={fetchError} />}
                    {!isLoading && !fetchError && groupedErrors.length === 0 && (
                         <div className="info-message">
                            <p>No recent validation errors found for this interface.</p>
                        </div>
                    )}
                    {!isLoading && !fetchError && groupedErrors.length > 0 && (
                        <div className="error-groups-container">
                            {groupedErrors.map(([filename, errorList]) => (
                                <div key={filename} className="error-file-group">
                                    <h4 className="error-filename-title">{filename}</h4>
                                    <ul className="error-list">
                                        {errorList.map(error => (
                                            <li key={error.id} className="error-list-item">
                                                <div className="error-meta">
                                                    {/* Added the uploader's name here since the backend provides it! */}
                                                    <span>Uploaded by <strong>{error.uploader || 'Unknown'}</strong> on {formatErrorDate(error.uploadedAt)}</span>
                                                </div>
                                                
                                                <div className="error-details-container">
                                                    {error.details ? (
                                                        (() => {
                                                            let parsedData = null;
                                                            try {
                                                                // Try to parse the new JSON format
                                                                parsedData = JSON.parse(error.details);
                                                            } catch (e) {
                                                                parsedData = null; // Fall back to legacy text
                                                            }

                                                            if (Array.isArray(parsedData)) {
                                                                // --- NEW JSON RENDERING ---
                                                                return (
                                                                    <div className="error-groups-wrapper">
                                                                        {parsedData.map((category, cIdx) => {
                                                                            const hiddenPatterns = category.total_patterns - (category.patterns ? category.patterns.length : 0);
                                                                            return (
                                                                                <div key={cIdx} className="error-group-block">
                                                                                    <div className="error-group-title">
                                                                                        {cIdx + 1}. {category.category}
                                                                                        <span style={{ fontSize: '0.8rem', color: 'var(--light-text)', marginLeft: '8px', fontWeight: 'normal' }}>
                                                                                            (Total: {category.total_patterns.toLocaleString()} unique patterns)
                                                                                        </span>
                                                                                    </div>
                                                                                    {category.patterns && category.patterns.length > 0 && (
                                                                                        <ul className="error-group-list">
                                                                                            {category.patterns.map((pat, pIdx) => {
                                                                                                if (pat.total_rows === 0) return <li key={pIdx} className="error-line">{pat.description}</li>;
                                                                                                
                                                                                                const hiddenRows = pat.total_rows - (pat.rows ? pat.rows.length : 0);
                                                                                                const rowStr = `Rows ${(pat.rows || []).join(', ')}` + (hiddenRows > 0 ? ` and ${hiddenRows.toLocaleString()} more rows` : '');

                                                                                                let cleanDesc = (pat.description || '').replace(/^:\s*/, '');
                                                                                                if (cleanDesc.includes("is an exact duplicate")) {
                                                                                                    if (pat.total_rows > 1) cleanDesc = cleanDesc.replace("is an exact duplicate", "are exact duplicates");
                                                                                                    return <li key={pIdx} className="error-line"><strong>{rowStr}</strong> {cleanDesc}</li>;
                                                                                                }
                                                                                                return <li key={pIdx} className="error-line"><strong>{rowStr}:</strong> {cleanDesc}</li>;
                                                                                            })}
                                                                                        </ul>
                                                                                    )}
                                                                                    {hiddenPatterns > 0 && (
                                                                                        <div className="more-errors-text" style={{ fontStyle: 'italic', color: '#fca5a5', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                                                                                            ... and {hiddenPatterns.toLocaleString()} more unique patterns omitted.
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                );
                                                            } else {
                                                                // --- LEGACY TEXT RENDERING (Fallback) ---
                                                                const errorGroups = error.details.includes('\n') ? error.details.split('\n\n') : [error.details];
                                                                
                                                                return (
                                                                    <div className="error-groups-wrapper">
                                                                        {errorGroups.map((group, gIdx) => {
                                                                            const lines = group.split('\n');
                                                                            const title = lines[0];
                                                                            const rawItems = lines.slice(1);
                                                                            const items = groupErrorItems(rawItems, 100);
                                            
                                                                            return (
                                                                                <div key={gIdx} className="error-group-block">
                                                                                    <div className="error-group-title">
                                                                                        {gIdx + 1}. {title}
                                                                                    </div>
                                                                                    {items.length > 0 && (
                                                                                        <ul className="error-group-list">
                                                                                            {items.map((item, iIdx) => {
                                                                                                const colonIndex = item.indexOf(':');
                                                                                                if (item.startsWith('Row') && colonIndex !== -1) {
                                                                                                    return (
                                                                                                        <li key={iIdx} className="error-line">
                                                                                                            <strong>{item.substring(0, colonIndex + 1)}</strong> {item.substring(colonIndex + 1)}
                                                                                                        </li>
                                                                                                    );
                                                                                                }
                                                                                                return <li key={iIdx} className="error-line">{item}</li>;
                                                                                            })}
                                                                                        </ul>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                );
                                                            }
                                                        })()
                                                    ) : (
                                                        <p className="error-details-empty">No detailed error information available.</p>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// DashboardCard logic remains unchanged, styling is handled in <style>
const DashboardCard = ({ item, onCardClick }) => {
    // --- UPDATED --- Using dark theme color variables/mapping
    const colorClassMap = {
        green: 'status-completed',
        yellow: 'status-in-progress',
        red: 'status-delayed', // Map red to delayed style
        blue: 'status-not-started',
        purple: 'status-failed-validation' // Map purple to failed style
    };

    const statusClassName = colorClassMap[item.status_color] || 'status-unknown';
    const isFailed = item.status_text.startsWith('Failed validation');

    const deadlineIsNear = useMemo(() => isDeadlineApproaching(item.pending_task_end_date), [item.pending_task_end_date]);

    return (
        <div
            // --- UPDATED --- Classes for dark theme card styling
            className={`content-card dashboard-card ${statusClassName} ${isFailed ? 'clickable-card' : ''}`}
            onClick={isFailed ? () => onCardClick(item.interface_name, item.raw_interface_name) : undefined}
        >
            {item.is_delayed && (
                <div className="card-delayed-stamp">DELAYED</div>
            )}
            {deadlineIsNear && !item.is_delayed && (
                <div className="card-deadline-warning" title="Deadline is approaching!">
                    &#9888; {/* Warning Symbol */}
                </div>
            )}
            <div>
                <h3 className="card-title">{item.interface_name}</h3>
                <p className={`card-status-text ${statusClassName}`}>{item.status_text}</p>
                {item.pending_task_name && (
                    <p className="card-pending-task">
                        Next step: <strong>{item.pending_task_name}</strong>
                    </p>
                )}
                {item.pending_task_end_date && (
                     <p className="card-deadline-date">
                        Deadline: {new Date(item.pending_task_end_date + 'T00:00:00Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })}
                    </p>
                )}
            </div>
            <div className="card-bottom-section">
                <div className="card-stats-container">
                    <div className="card-stat-item" title="Disapprovals">
                        {/* --- UPDATED --- Using FaExclamationTriangle */}
                        <FaExclamationTriangle className="card-icon disapproval-icon" />
                        <span>{item.disapproval_count}</span>
                    </div>
                    <div className="card-stat-item" title="Reopenings">
                        {/* --- UPDATED --- Using FaSyncAlt */}
                        <FaSyncAlt className="card-icon reopening-icon" />
                        <span>{item.reopening_count}</span>
                    </div>
                </div>
                {isFailed && (
                    <div className="card-click-prompt">
                        Check validation errors &gt;
                    </div>
                )}
            </div>
        </div>
    );
};


// --- MAIN PAGE COMPONENT ---
export default function StatusDashboard() {
    const navigate = useNavigate();

    const { user, selectedProjectName } = useAuth();
    const userProjects = user?.projects || [];

    const [dashboardData, setDashboardData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortOption, setSortOption] = useState('status_completed_first');
    const [activeFilters, setActiveFilters] = useState({
        failed: false,
        delayed: false,
        deadline: false,
        pendingWysupp: false,
        pendingClient: false,
        notStarted: false,
        inProgress: false,
        completed: false
    });

    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [selectedInterfaceForErrors, setSelectedInterfaceForErrors] = useState(null);
    const [errorHistory, setErrorHistory] = useState([]);
    const [isLoadingErrors, setIsLoadingErrors] = useState(false);
    const [errorFetchError, setErrorFetchError] = useState(null);

    // useEffect hooks remain unchanged
    useEffect(() => {
        if (!selectedProjectName) {
            setDashboardData([]);
            if (user) {
                setLoading(false);
            } else {
                 setLoading(true); // Still waiting for user context
            }
            return;
        }

        const fetchDashboardData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axiosInstance.get(`/api/projects/${selectedProjectName}/dashboard`);
                setDashboardData(response.data.items || []);
                setLoading(false); // Stop loading on success
            } catch (err) {
                // --- FIX: Prevent red error flash on timeout ---
                if (err.response?.status === 401) return;
                
                setError(err.response?.data?.detail || 'Failed to load dashboard data.');
                setDashboardData([]);
                setLoading(false); // Stop loading on actual errors
            }
        };
        // Only fetch if user context is loaded AND a project is selected
        if (user) {
             fetchDashboardData();
        }
    }, [selectedProjectName, user]);


    // Handlers and Memos remain unchanged
    const handleShowErrors = async (displayName, rawInterfaceName) => {
        setSelectedInterfaceForErrors(displayName);
        setIsErrorModalOpen(true);
        setIsLoadingErrors(true);
        setErrorFetchError(null);
        setErrorHistory([]);
        try {
            const response = await axiosInstance.get(`/api/projects/${selectedProjectName}/interfaces/${rawInterfaceName}/errors`);
            const uniqueErrorsMap = new Map();
            for (const error of response.data) {
                const uniqueKey = `${error.filename}|${error.details}|${error.uploaded_at}`;
                if (!uniqueErrorsMap.has(uniqueKey)) {
                    uniqueErrorsMap.set(uniqueKey, error);
                }
            }
            const sortedErrors = Array.from(uniqueErrorsMap.values()).sort((a, b) =>
                new Date(b.uploaded_at) - new Date(a.uploaded_at)
            );
            setErrorHistory(sortedErrors);
            setIsLoadingErrors(false); // Stop loading on success
        } catch (err) {
            // --- FIX: Prevent error flash inside modal on timeout ---
            if (err.response?.status === 401) return;
            
            setErrorFetchError(err.response?.data?.detail || 'Failed to fetch error history.');
            setIsLoadingErrors(false); // Stop loading on actual errors
        }
    };

    const processedDashboardData = useMemo(() => {
        let filteredData = dashboardData.filter(item => {
            // 1. Status Group (OR Logic: Item must match at least one selected status)
            // ---> FIX: 'failed' is now part of the status group <---
            const isStatusFilterActive = activeFilters.completed || activeFilters.inProgress || activeFilters.notStarted || activeFilters.failed;
            let passesStatus = !isStatusFilterActive; // Pass automatically if no status filter is active
            if (isStatusFilterActive) {
                if (activeFilters.completed && item.status_text.startsWith('Completed')) passesStatus = true;
                if (activeFilters.inProgress && item.status_text.startsWith('In Progress')) passesStatus = true;
                if (activeFilters.notStarted && item.status_text.startsWith('Not Started')) passesStatus = true;
                if (activeFilters.failed && item.status_text.startsWith('Failed validation')) passesStatus = true;
            }

            // 2. Responsibility Group (OR Logic: Item must match at least one selected responsibility)
            const isRespFilterActive = activeFilters.pendingWysupp || activeFilters.pendingClient;
            let passesResp = !isRespFilterActive; // Pass automatically if no responsibility filter is active
            if (isRespFilterActive) {
                if (activeFilters.pendingWysupp && item.status_text.includes('Pending at Wysupp')) passesResp = true;
                if (activeFilters.pendingClient && item.status_text.includes('Pending at client')) passesResp = true;
            }

            // 3. Problem/Alert Group (AND Logic: Item must match ALL selected alerts)
            // ---> FIX: 'failed' removed from here <---
            let passesAlerts = true;
            if (activeFilters.delayed && !item.is_delayed) passesAlerts = false;
            if (activeFilters.deadline && !isDeadlineApproaching(item.pending_task_end_date)) passesAlerts = false;

            // Item must pass all three categorical group checks to be included in the final view
            return passesStatus && passesResp && passesAlerts;
        });

        // Sorting logic remains exactly the same
        switch (sortOption) {
            case 'disapprovals': filteredData.sort((a, b) => b.disapproval_count - a.disapproval_count); break;
            case 'reopenings': filteredData.sort((a, b) => b.reopening_count - a.reopening_count); break;
            case 'status_completed_first': { const statusOrder = { green: 1, yellow: 2, blue: 3, red: 4, purple: 5 }; filteredData.sort((a, b) => (statusOrder[a.status_color] || 99) - (statusOrder[b.status_color] || 99)); break; }
            case 'status_failed_first': { const statusOrder = { purple: 1, red: 2, yellow: 3, blue: 4, green: 5 }; filteredData.sort((a, b) => (statusOrder[a.status_color] || 99) - (statusOrder[b.status_color] || 99)); break; }
            default: filteredData.sort((a, b) => a.interface_name.localeCompare(b.interface_name)); break;
        }
        
        return filteredData;
    }, [dashboardData, sortOption, activeFilters]);

    const handleFilterToggle = (filterName) => {
        setActiveFilters(prev => ({ ...prev, [filterName]: !prev[filterName] }));
    };

    // --- UPDATED --- Use dark theme spinner if waiting for user context
    if (!user) return <LoadingSpinner />;

    return (
        // --- UPDATED --- Changed main container class
        <div className="admin-page-container">
            <Header />

            {/* --- UPDATED --- Changed main content class */}
            <main className="admin-main-content">
                {/* --- UPDATED --- Changed header class */}
                <div className="page-header">
                    <button onClick={() => navigate('/home')} className="back-button">
                        <FaArrowLeft /> Back
                    </button>
                    
                    {/* --- NEW: Grouping for inline title and project name --- */}
                    <div className="header-title-group">
                        <h1 className="page-title">Interface Status Dashboard</h1>
                        {selectedProjectName && (
                            <div className="project-context-inline">
                                <span className="separator">|</span>
                                <span className="context-label">Project:</span>
                                <span className="project-highlight">{selectedProjectName}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* --- REMOVED --- Local project selector */}

                {selectedProjectName && (
                    // --- UPDATED --- Changed class for dark theme
                    <div className="controls-container filter-sort-container content-card">
                        <div className="sort-wrapper">
                            <label htmlFor="sort-selector" className="control-label">Sort by:</label>
                            <select id="sort-selector" value={sortOption} onChange={(e) => setSortOption(e.target.value)} className="control-dropdown">
                                <option value="status_completed_first">Status (Completed First)</option>
                                <option value="status_failed_first">Status (Problem First)</option>
                                <option value="disapprovals">Most Disapprovals</option>
                                <option value="reopenings">Most Reopenings</option>
                            </select>
                        </div>
                        <div className="filter-wrapper">
                            <span className="control-label">Filters:</span>
                            <div className="filter-groups-container">
                                
                                <div className="filter-category">
                                    <span className="filter-category-label">Status</span>
                                    <div className="filter-buttons">
                                        <button onClick={() => handleFilterToggle('completed')} className={`filter-button ${activeFilters.completed ? 'active' : ''}`}>Completed</button>
                                        <button onClick={() => handleFilterToggle('inProgress')} className={`filter-button ${activeFilters.inProgress ? 'active' : ''}`}>In Progress</button>
                                        <button onClick={() => handleFilterToggle('notStarted')} className={`filter-button ${activeFilters.notStarted ? 'active' : ''}`}>Not Started</button>
                                        <button onClick={() => handleFilterToggle('failed')} className={`filter-button filter-failed ${activeFilters.failed ? 'active' : ''}`}>Failed</button>
                                    </div>
                                </div>

                                <div className="filter-category">
                                    <span className="filter-category-label">Assigned To</span>
                                    <div className="filter-buttons">
                                        <button onClick={() => handleFilterToggle('pendingWysupp')} className={`filter-button ${activeFilters.pendingWysupp ? 'active' : ''}`}>Wysupp</button>
                                        <button onClick={() => handleFilterToggle('pendingClient')} className={`filter-button ${activeFilters.pendingClient ? 'active' : ''}`}>Client</button>
                                    </div>
                                </div>

                                <div className="filter-category">
                                    <span className="filter-category-label">Alerts</span>
                                    <div className="filter-buttons">
                                        <button onClick={() => handleFilterToggle('delayed')} className={`filter-button filter-delayed ${activeFilters.delayed ? 'active' : ''}`}>Delayed</button>
                                        <button onClick={() => handleFilterToggle('deadline')} className={`filter-button filter-deadline ${activeFilters.deadline ? 'active' : ''}`}>Deadline</button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <ErrorMessage message={error} />
                ) : !selectedProjectName && userProjects.length > 0 ? (
                     <div className="info-message"><p>Please select a project from the header to view its status.</p></div>
                ) : !selectedProjectName && userProjects.length === 0 ? (
                     <div className="info-message"><p>You are not assigned to any projects.</p></div>
                ) : processedDashboardData.length > 0 ? (
                    <div className="dashboard-grid">
                        {processedDashboardData.map(item => (
                            <DashboardCard key={item.raw_interface_name} item={item} onCardClick={handleShowErrors} />
                        ))}
                    </div>
                ) : (
                    <div className="info-message">
                        <p>No interfaces match the current filters for {selectedProjectName}.</p>
                    </div>
                )}
            </main>

            <ValidationErrorModal
                isOpen={isErrorModalOpen}
                onClose={() => setIsErrorModalOpen(false)}
                interfaceName={selectedInterfaceForErrors}
                errors={errorHistory}
                isLoading={isLoadingErrors}
                fetchError={errorFetchError}
            />

            {/* --- UPDATED --- Added dark theme styles */}
            <style>{`
                 /* --- Dark Theme Variables --- */
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                    --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1); --danger-color: #ef4444;
                    --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1);
                    --blue-accent: #60a5fa; --blue-bg: rgba(59, 130, 246, 0.1);
                    --yellow-accent: #fcd34d; --yellow-bg: rgba(245, 158, 11, 0.1);
                    --orange-accent: #fb923c; --orange-bg: rgba(251, 146, 60, 0.1); /* For Deadline */
                }

                /* Page Layout & Header */
                .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; color: var(--text-color); font-family: 'Inter', sans-serif; }
                .admin-main-content { max-width: 1400px; margin: 2rem auto; padding: 0 2rem; }
                .back-button {
                    background: var(--card-bg);
                    color: var(--text-color);
                    border: 1px solid var(--border-color);
                    padding: 0.5rem 1rem;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    flex-shrink: 0; /* Prevent shrinking */
                }
                .back-button:hover { background-color: #4a5568; }
                .page-header {
                    display: flex;
                    align-items: center; /* Vertically align items */
                    gap: 1rem; /* Space between button and title */
                    margin-bottom: 2rem;
                    padding-bottom: 1rem;
                    border-bottom: 1px solid var(--border-color);
                }
                .header-title-group {
                    display: flex;
                    align-items: center;
                    flex-grow: 1;
                    flex-wrap: wrap;
                    gap: 1rem;
                }

                /* Updated page-title to remove flex-grow (parent handles it now) and fix margins */
                .page-title {
                    font-size: 2rem;
                    font-weight: 700;
                    color: var(--text-color);
                    margin: 0;
                    line-height: 1;
                    white-space: nowrap;
                }

                /* Styles for the project name display */
                .project-context-inline {
                    display: flex;
                    align-items: center;
                    font-size: 1rem;
                    color: var(--light-text);
                    font-weight: 500;
                    white-space: nowrap;
                }

                .separator {
                    margin-right: 1rem;
                    color: var(--border-color);
                    font-weight: 300;
                    font-size: 1.2rem;
                    position: relative;
                    top: -1px;
                }

                .context-label {
                    margin-right: 0.5rem;
                }

                .project-highlight {
                    color: var(--green-accent);
                    font-weight: 600;
                }

                /* --- REMOVED --- .project-selector-wrapper styles */

                 /* Controls */
                .filter-sort-container {
                    display: flex; flex-wrap: wrap; gap: 1.5rem 2.5rem; /* Adjusted gap */ align-items: flex-start; /* Align items top */
                    padding: 1.5rem 2rem; /* Increased padding */
                    /* background-color: var(--card-bg); */ /* Inherits from content-card */
                    /* border-radius: 12px; */ /* Inherits from content-card */
                    /* border: 1px solid var(--border-color); */ /* Inherits from content-card */
                    margin-bottom: 2rem;
                 }
                .control-label { display: block; font-size: 0.9rem; font-weight: 500; color: var(--light-text); margin-bottom: 0.5rem; }
                .control-dropdown {
                    display: block; width: 100%; min-width: 220px; /* Min width for dropdown */ padding: 0.6rem 1rem;
                    font-size: 0.9rem; border: 1px solid var(--border-color); border-radius: 6px;
                    background-color: var(--input-bg); color: var(--text-color); box-shadow: none;
                    -webkit-appearance: none; appearance: none;
                    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="%23a0aec0" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/></svg>');
                    background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 1em;
                    cursor: pointer;
                }
                .control-dropdown:focus { outline: none; border-color: var(--green-accent); }
                .filter-wrapper { display: flex; flex-direction: column; gap: 0.5rem; }

                /* --- NEW FILTER GROUP STYLES --- */
                .filter-groups-container { 
                    display: flex; 
                    flex-wrap: wrap; 
                    gap: 1.5rem; 
                    margin-top: 0.25rem;
                }
                .filter-category { 
                    display: flex; 
                    flex-direction: column; 
                    gap: 0.5rem; 
                    border-left: 2px solid var(--border-color); 
                    padding-left: 1rem; 
                }
                .filter-category:first-child {
                    border-left: none;
                    padding-left: 0;
                }
                .filter-category-label { 
                    font-size: 0.75rem; 
                    text-transform: uppercase; 
                    letter-spacing: 0.05em; 
                    color: var(--light-text); 
                    font-weight: 600; 
                }
                /* -------------------------------- */

                .filter-buttons { display: flex; gap: 0.75rem; flex-wrap: wrap; }
                .filter-button {
                    padding: 0.5rem 1rem; border: 1px solid var(--border-color); background-color: var(--input-bg);
                    color: var(--light-text); border-radius: 9999px; font-size: 0.875rem;
                    font-weight: 500; cursor: pointer; transition: all 0.2s ease-in-out;
                }
                .filter-button:hover { border-color: var(--light-text); background-color: var(--card-bg); color: var(--text-color); }
                .filter-button.active { background-color: var(--green-accent); color: var(--dark-bg); border-color: var(--green-accent); font-weight: 600; }
                /* Specific active styles for problem filters */
                 .filter-button.filter-failed.active { background-color: var(--danger-color); color: var(--text-color); border-color: var(--danger-color); }
                 .filter-button.filter-delayed.active { background-color: var(--danger-color); color: var(--text-color); border-color: var(--danger-color); } /* Using danger for delayed too */
                 .filter-button.filter-deadline.active { background-color: var(--orange-accent); color: var(--dark-bg); border-color: var(--orange-accent); }


                /* Dashboard Grid & Cards */
                .dashboard-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1.5rem; }
                .content-card { /* General card style used by InterfaceCard & DashboardCard */
                    background-color: var(--card-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 12px;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                    overflow: hidden; /* Important for border-radius */
                }
                .content-card.dashboard-card { /* Target the specific combo */
                    position: relative;
                    /* background-color: var(--card-bg); */ /* Inherited */
                    /* border-radius: 12px; */ /* Inherited */
                    /* border: 1px solid var(--border-color); */ /* Inherited */
                    padding: 1.5rem 1rem 1rem 1.5rem; /* Adjusted padding */
                    display: flex; flex-direction: column;
                    justify-content: space-between;
                    border-left-width: 5px; /* Thicker status border */
                    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
                 }
                 /* Status Colors via Border */
                 .dashboard-card.status-completed { border-left-color: var(--green-accent); }
                 .dashboard-card.status-in-progress { border-left-color: var(--yellow-accent); }
                 .dashboard-card.status-delayed { border-left-color: var(--danger-color); } /* Red */
                 .dashboard-card.status-not-started { border-left-color: var(--blue-accent); }
                 .dashboard-card.status-failed-validation { border-left-color: var(--danger-color); } /* Red */
                 .dashboard-card.status-unknown { border-left-color: var(--border-color); }


                .card-delayed-stamp {
                    position: absolute; top: 0; left: 0;
                    background-color: var(--danger-color); color: white;
                    padding: 0.2rem 1rem; font-size: 0.7rem; font-weight: 700;
                    border-top-left-radius: 11px; /* Match parent */ border-bottom-right-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
                .card-deadline-warning {
                    position: absolute; top: 0.75rem; right: 0.75rem;
                    font-size: 1.25rem; color: var(--orange-accent);
                }
                .card-title {
                    font-weight: 600; font-size: 1.2rem; /* Slightly larger */ color: var(--text-color);
                    margin-top: 0.5rem; margin-bottom: 0.5rem; word-break: break-word;
                }
                .card-status-text { font-weight: 500; font-size: 0.9rem; margin-bottom: 0.75rem; }
                 /* Status Text Colors */
                 .card-status-text.status-completed { color: var(--success-color); }
                 .card-status-text.status-in-progress { color: var(--yellow-accent); }
                 .card-status-text.status-delayed { color: var(--error-color); } /* Red */
                 .card-status-text.status-not-started { color: var(--blue-accent); }
                 .card-status-text.status-failed-validation { color: var(--error-color); } /* Red */
                 .card-status-text.status-unknown { color: var(--light-text); }

                .card-pending-task { margin-top: 0.5rem; font-size: 0.85rem; color: var(--light-text); }
                .card-pending-task strong { color: var(--text-color); }
                .card-deadline-date {
                    margin-top: 0.75rem; font-size: 0.8rem; font-weight: 500;
                    color: var(--light-text); background-color: var(--input-bg);
                    padding: 0.25rem 0.5rem; border-radius: 4px; display: inline-block;
                    border: 1px solid var(--border-color);
                 }

                .card-bottom-section {
                    margin-top: 1rem; padding-top: 0.75rem; border-top: 1px solid var(--border-color);
                    display: flex; justify-content: space-between; align-items: flex-end;
                }
                 .card-stats-container { display: flex; align-items: center; gap: 1rem; font-size: 0.9rem; color: var(--light-text); }
                .card-stat-item { display: flex; align-items: center; gap: 0.3rem; }
                .card-icon { height: 1.1rem; width: 1.1rem; color: var(--light-text); }
                .card-icon.disapproval-icon { color: var(--error-color); }
                .card-icon.reopening-icon { color: var(--blue-accent); }
                .card-click-prompt {
                    font-size: 0.8rem; font-weight: 600; color: var(--blue-accent);
                    align-self: flex-end;
                }

                /* Utility, Messages & Modal */
                .loading-container { display:flex; flex-direction:column; align-items:center; justify-content:center; padding: 3rem; color:var(--light-text); }
                .loading-spinner { width: 40px; height: 40px; margin: 0 auto 1rem; border: 4px solid var(--border-color); border-top-color: var(--green-accent); border-radius: 50%; animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }
                .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); padding: 1rem; border-radius: 8px; text-align: center; border: 1px solid var(--danger-color); }
                 .info-message {
                     background-color: var(--card-bg); border: 1px solid var(--border-color);
                     color: var(--light-text); padding: 1.5rem; border-radius: 8px;
                     text-align: center; margin-top: 2rem;
                 }

                .clickable-card { cursor: pointer; }
                .clickable-card:hover { transform: translateY(-3px); box-shadow: 0 8px 12px -4px rgba(0, 0, 0, 0.2), 0 4px 6px -4px rgba(0, 0, 0, 0.1); }

                /* Modal Styles */
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.7); display: flex; justify-content: center; align-items: center; z-index: 1000; padding: 1rem; }
                .modal-content { background: var(--card-bg); border: 1px solid var(--border-color); padding: 0; border-radius: 8px; width: 90%; max-width: 700px; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); animation: fadeInScaleUp 0.2s ease-out; }
                .modal-content.large-modal { max-width: 900px; /* Wider modal */ }
                .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; border-bottom: 1px solid var(--border-color); }
                .modal-header h3 { font-size: 1.25rem; font-weight: 600; color: var(--text-color); margin: 0; }
                .modal-close-button { background: transparent; border: none; font-size: 1.5rem; cursor: pointer; color: var(--light-text); padding: 0 0.5rem; }
                .modal-body { overflow-y: auto; padding: 1.5rem; color: var(--text-color); }
                .modal-body::-webkit-scrollbar { width: 8px; }
                .modal-body::-webkit-scrollbar-track { background: var(--input-bg); }
                .modal-body::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 8px; }

                @keyframes fadeInScaleUp { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }

                /* Error Modal Specific */
                .error-groups-container { max-height: 65vh; overflow-y: auto; padding-right: 0.5rem; }
                .error-file-group { margin-bottom: 1.5rem; background: var(--input-bg); border-radius: 8px; border: 1px solid var(--border-color); overflow: hidden; }
                .error-filename-title {
                    font-size: 1.1rem; font-weight: 600; color: var(--light-text);
                    padding: 0.75rem 1rem; border-bottom: 1px solid var(--border-color);
                    margin: 0; background-color: var(--card-bg);
                }
                .error-list { list-style: none; padding: 0; margin: 0; }
                .error-list-item {
                    background: var(--error-bg); border-left: 4px solid var(--danger-color);
                    padding: 1rem; margin: 1rem; border-radius: 4px;
                }
                .error-meta { display: flex; flex-wrap: wrap; justify-content: space-between; font-size: 0.8rem; color: var(--light-text); margin-bottom: 0.5rem; }
                /* --- NEW ERROR FORMATTING --- */
                .error-groups-wrapper { display: flex; flex-direction: column; gap: 0.75rem; }
                .error-group-block { background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 6px; padding: 0.75rem 1rem; }
                .error-group-title { font-weight: 600; color: var(--error-color); margin-bottom: 0.5rem; font-size: 0.95rem; border-bottom: 1px solid var(--border-color); padding-bottom: 0.25rem; }
                .error-group-list { list-style-type: none; margin: 0; padding: 0; }
                
                .error-details-container {
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                    border-left: 4px solid var(--danger-color);
                    border-radius: 6px;
                    padding: 1rem;
                    margin-top: 0.75rem;
                    max-height: 350px; /* Increased slightly for better reading */
                    overflow-y: auto;
                }

                .error-groups-container::-webkit-scrollbar { width: 8px; }
                .error-groups-container::-webkit-scrollbar-track { background: var(--input-bg); border-radius: 8px; }
                .error-groups-container::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 8px; }
                .error-groups-container::-webkit-scrollbar-thumb:hover { background-color: var(--light-text); }

                /* And let's add the hover state to the modal-body scrollbar too just in case! */
                .modal-body::-webkit-scrollbar-thumb:hover { background-color: var(--light-text); }
                
                .error-line {
                    font-size: 0.85rem;
                    color: var(--light-text);
                    margin-bottom: 0.25rem;
                    line-height: 1.4;
                    word-break: break-word;
                    font-family: monospace;
                    position: relative;
                    padding-left: 1rem;
                }
                
                /* Adds a nice little bullet point to the monospace list */
                .error-line::before {
                    content: "›";
                    position: absolute;
                    left: 0;
                    color: var(--danger-color);
                    font-weight: bold;
                }
                
                .error-line:last-child { margin-bottom: 0; }
                .error-line strong { color: var(--error-color); font-weight: 700; }
                .error-details-empty { font-size: 0.85rem; color: var(--light-text); font-style: italic; margin: 0; }
                
                /* Custom scrollbar */
                .error-details-container::-webkit-scrollbar { width: 8px; }
                .error-details-container::-webkit-scrollbar-track { background: var(--input-bg); border-radius: 8px; }
                .error-details-container::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 8px; }
                .error-details-container::-webkit-scrollbar-thumb:hover { background-color: var(--light-text); }


                 /* Responsive Adjustments */
                 @media (max-width: 768px) {
                    .page-header {
                        flex-wrap: wrap; /* Allow wrapping */
                    }
                    .page-title {
                        flex-basis: 100%; /* Make title take full width below button */
                        order: 2; /* Ensure title comes after button visually if wrapped */
                        text-align: center;
                        margin-top: 0.5rem;
                    }
                    .back-button {
                        order: 1;
                    }
                    .admin-main-content { padding: 0 1rem; }
                    .filter-sort-container { flex-direction: column; align-items: stretch; }
                    .filter-wrapper { flex-direction: column; align-items: flex-start; }
                    .filter-buttons { margin-top: 0.5rem; }
                    .modal-content { max-width: 95%; }
                 }

            `}</style>
        </div>
    );
}